import { create } from "zustand";
import { API_URL } from "../services/apiUrl";

/**
 * Connectivity Store
 *
 * Whether the app is working against the live API ("online") or against the
 * copy saved on this device ("offline"). Offline is always read-only:
 * tripStore.isReadOnly(), the API client and the save queues all check it.
 */

export type ConnectivityMode = "online" | "offline";

interface ConnectivityState {
  mode: ConnectivityMode;
  /** The app booted from the saved copy without a Clerk session. */
  startedOffline: boolean;
  /** When the saved copy on this device was last updated (ISO 8601). */
  savedAt: string | null;
  /** Started offline and the API answers again: offer a reload. */
  reconnectAvailable: boolean;
}

export const useConnectivityStore = create<ConnectivityState>(() => ({
  mode: "online",
  startedOffline: false,
  savedAt: null,
  reconnectAvailable: false,
}));

export function isOfflineMode(): boolean {
  return useConnectivityStore.getState().mode === "offline";
}

export function setSavedAt(savedAt: string | null): void {
  useConnectivityStore.setState({ savedAt });
}

/** Boot straight into the saved copy (no Clerk session is available). */
export function enterStartedOfflineMode(savedAt: string | null): void {
  useConnectivityStore.setState({
    mode: "offline",
    startedOffline: true,
    savedAt,
  });
  scheduleReconnectCheck();
}

/**
 * The API client saw a network failure (not an HTTP error), or the browser
 * reported going offline. Unsaved edits stay queued until the API answers.
 */
export function reportNetworkFailure(): void {
  if (isOfflineMode()) return;
  useConnectivityStore.setState({ mode: "offline" });
  scheduleReconnectCheck();
}

// ── Reconnection ──

const RECONNECT_POLL_MS = 30_000;
const HEALTH_TIMEOUT_MS = 5_000;

type ReconnectListener = () => void;
const reconnectListeners = new Set<ReconnectListener>();
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let checking: Promise<void> | null = null;

/** Called when a session that lost its connection mid-way gets it back. */
export function onReconnect(listener: ReconnectListener): () => void {
  reconnectListeners.add(listener);
  return () => reconnectListeners.delete(listener);
}

async function isApiReachable(): Promise<boolean> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
  try {
    const response = await fetch(`${API_URL}/health`, {
      cache: "no-store",
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

function scheduleReconnectCheck(delay = RECONNECT_POLL_MS): void {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = setTimeout(() => {
    pollTimer = null;
    void checkConnection();
  }, delay);
}

/** Probe the API's health endpoint and leave offline mode if it answers. */
export function checkConnection(): Promise<void> {
  if (checking) return checking;
  checking = (async () => {
    const state = useConnectivityStore.getState();
    if (state.mode === "online" || state.reconnectAvailable) return;
    if (!(await isApiReachable())) {
      scheduleReconnectCheck();
      return;
    }
    if (state.startedOffline) {
      // There is no Clerk session to resume, so editing needs a reload.
      useConnectivityStore.setState({ reconnectAvailable: true });
      return;
    }
    useConnectivityStore.setState({ mode: "online" });
    for (const listener of reconnectListeners) listener();
  })().finally(() => {
    checking = null;
  });
  return checking;
}

let watching = false;

/** Install the browser online/offline/visibility listeners once. */
export function startConnectivityWatcher(): void {
  if (watching || typeof window === "undefined") return;
  watching = true;
  window.addEventListener("offline", reportNetworkFailure);
  window.addEventListener("online", () => void checkConnection());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && isOfflineMode()) {
      void checkConnection();
    }
  });
}
